// API helpers for the Surveys feature of the KSP Training tile.
// Mirrors the conventions in ../api.js (trainingApi).
const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' ? 'http://localhost:5000/api' : '/api');

const authHeaders = (extra = {}) => {
  const token = localStorage.getItem('token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    ...extra,
  };
};

const handle = async (res) => {
  if (!res.ok) {
    let detail = '';
    try { const j = await res.json(); detail = j.message || j.details || ''; } catch (_) {}
    throw new Error(detail || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
};

// Results scope: { round_id: <id>|'all', language } -> "?round_id=..&language=.."
const scopeQuery = (scope = {}) => {
  const params = new URLSearchParams();
  if (scope.round_id !== undefined && scope.round_id !== null && scope.round_id !== '') params.set('round_id', scope.round_id);
  if (scope.language) params.set('language', scope.language);
  const q = params.toString();
  return q ? `?${q}` : '';
};
const langQuery = (lang) => (lang ? `?lang=${encodeURIComponent(lang)}` : '');

const jsonBody = (method, data) => ({
  method,
  headers: authHeaders({ 'Content-Type': 'application/json' }),
  body: JSON.stringify(data),
});

export const surveyApi = {
  // ----- User -----
  listSurveys: (lang) =>
    fetch(`${API_URL}/surveys${langQuery(lang)}`, { headers: authHeaders() }).then(handle),
  getSurvey: (id, lang) =>
    fetch(`${API_URL}/surveys/${id}${langQuery(lang)}`, { headers: authHeaders() }).then(handle),
  startResponse: (id, language) =>
    fetch(`${API_URL}/surveys/${id}/responses`, jsonBody('POST', { language })).then(handle),
  submitResponse: (responseId, answers, durationSeconds) =>
    fetch(`${API_URL}/surveys/responses/${responseId}/submit`,
      jsonBody('POST', { answers, duration_seconds: durationSeconds })).then(handle),
  myHistory: () =>
    fetch(`${API_URL}/surveys/me/history`, { headers: authHeaders() }).then(handle),
  surveyForTraining: (trainingId) =>
    fetch(`${API_URL}/surveys/for-training/${trainingId}`, { headers: authHeaders() }).then(handle),
  // Auth-gated image URL; pass the filename as `v` to bust the cache when it changes.
  questionImageUrl: (surveyId, qid, v) =>
    `${API_URL}/surveys/${surveyId}/questions/${qid}/image${v ? `?v=${encodeURIComponent(v)}` : ''}`,
  // Auth-gated URL for a ranking-item image (stored by filename in the survey's dir).
  surveyImageUrl: (surveyId, filename) =>
    `${API_URL}/surveys/${surveyId}/image/${encodeURIComponent(filename)}`,

  // ----- Admin -----
  adminListAll: (archived = false) =>
    fetch(`${API_URL}/surveys/admin/all${archived ? '?archived=1' : ''}`, { headers: authHeaders() }).then(handle),
  adminClone: (id) =>
    fetch(`${API_URL}/surveys/admin/${id}/clone`, { method: 'POST', headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(handle),
  adminRestore: (id) =>
    fetch(`${API_URL}/surveys/admin/${id}/restore`, { method: 'PUT', headers: authHeaders({ 'Content-Type': 'application/json' }) }).then(handle),
  adminHardDelete: (id) =>
    fetch(`${API_URL}/surveys/admin/${id}/permanent`, { method: 'DELETE', headers: authHeaders() }).then(handle),
  adminAudienceOptions: () =>
    fetch(`${API_URL}/surveys/admin/audience-options`, { headers: authHeaders() }).then(handle),
  adminGet: (id) =>
    fetch(`${API_URL}/surveys/admin/${id}`, { headers: authHeaders() }).then(handle),
  adminCreate: (data) =>
    fetch(`${API_URL}/surveys/admin`, jsonBody('POST', data)).then(handle),
  adminUpdate: (id, data) =>
    fetch(`${API_URL}/surveys/admin/${id}`, jsonBody('PUT', data)).then(handle),
  adminDelete: (id) =>
    fetch(`${API_URL}/surveys/admin/${id}`, { method: 'DELETE', headers: authHeaders() }).then(handle),
  adminUpsertQuestion: (id, payload) =>
    fetch(`${API_URL}/surveys/admin/${id}/questions`, jsonBody('POST', payload)).then(handle),
  adminDeleteQuestion: (qid) =>
    fetch(`${API_URL}/surveys/admin/questions/${qid}`, { method: 'DELETE', headers: authHeaders() }).then(handle),
  adminUploadQuestionImage: (surveyId, qid, file) => {
    const form = new FormData();
    form.append('image', file);
    return fetch(`${API_URL}/surveys/admin/${surveyId}/questions/${qid}/image`,
      { method: 'POST', headers: authHeaders(), body: form }).then(handle);
  },
  adminUploadRankingItemImage: (surveyId, qid, optionValue, file) => {
    const form = new FormData();
    form.append('image', file);
    form.append('option_value', String(optionValue));
    return fetch(`${API_URL}/surveys/admin/${surveyId}/questions/${qid}/item-image`,
      { method: 'POST', headers: authHeaders(), body: form }).then(handle);
  },
  adminUpdateQuestionImage: (surveyId, qid, settings) =>
    fetch(`${API_URL}/surveys/admin/${surveyId}/questions/${qid}/image`, jsonBody('PUT', settings)).then(handle),
  adminDeleteQuestionImage: (surveyId, qid) =>
    fetch(`${API_URL}/surveys/admin/${surveyId}/questions/${qid}/image`, { method: 'DELETE', headers: authHeaders() }).then(handle),
  adminReorderQuestions: (id, order) =>
    fetch(`${API_URL}/surveys/admin/${id}/questions/reorder`, jsonBody('PUT', { order })).then(handle),
  adminImportQuestions: (id, questions, replace = false) =>
    fetch(`${API_URL}/surveys/admin/${id}/questions/import`, jsonBody('POST', { questions, replace })).then(handle),
  adminResults: (id, scope) =>
    fetch(`${API_URL}/surveys/admin/${id}/results${scopeQuery(scope)}`, { headers: authHeaders() }).then(handle),
  adminTrends: (id, language) =>
    fetch(`${API_URL}/surveys/admin/${id}/trends${scopeQuery({ language })}`, { headers: authHeaders() }).then(handle),
  adminExport: (id, scope) =>
    fetch(`${API_URL}/surveys/admin/${id}/export${scopeQuery(scope)}`, { headers: authHeaders() }).then(handle),
  adminResponses: (id, scope) =>
    fetch(`${API_URL}/surveys/admin/${id}/responses${scopeQuery(scope)}`, { headers: authHeaders() }).then(handle),
  adminResponseDetail: (id, rid) =>
    fetch(`${API_URL}/surveys/admin/${id}/responses/${rid}`, { headers: authHeaders() }).then(handle),
  adminNonRespondents: (id, scope) =>
    fetch(`${API_URL}/surveys/admin/${id}/non-respondents${scopeQuery(scope)}`, { headers: authHeaders() }).then(handle),

  // ----- Rounds (re-run a survey; each round keeps its own results) -----
  adminStartRound: (id, data) =>
    fetch(`${API_URL}/surveys/admin/${id}/rounds`, jsonBody('POST', data)).then(handle),
  adminUpdateRound: (id, rid, data) =>
    fetch(`${API_URL}/surveys/admin/${id}/rounds/${rid}`, jsonBody('PUT', data)).then(handle),

  // ----- Translations (text only; question/option identity is shared) -----
  adminGetTranslations: (id) =>
    fetch(`${API_URL}/surveys/admin/${id}/translations`, { headers: authHeaders() }).then(handle),
  adminSaveTranslation: (id, lang, data) =>
    fetch(`${API_URL}/surveys/admin/${id}/translations/${lang}`, jsonBody('PUT', data)).then(handle),
  adminDeleteTranslation: (id, lang) =>
    fetch(`${API_URL}/surveys/admin/${id}/translations/${lang}`, { method: 'DELETE', headers: authHeaders() }).then(handle),

  // ----- Reviewer-only (identity-gated; 403 for everyone but the designated reviewer) -----
  adminIdentifiedResponses: (id, scope) =>
    fetch(`${API_URL}/surveys/admin/${id}/identified-responses${scopeQuery(scope)}`, { headers: authHeaders() }).then(handle),
  adminIdentifiedResponseDetail: (id, rid) =>
    fetch(`${API_URL}/surveys/admin/${id}/identified-responses/${rid}`, { headers: authHeaders() }).then(handle),
  adminIdentifiedNonRespondents: (id, scope) =>
    fetch(`${API_URL}/surveys/admin/${id}/identified-non-respondents${scopeQuery(scope)}`, { headers: authHeaders() }).then(handle),
  adminIdentifiedExport: (id, scope) =>
    fetch(`${API_URL}/surveys/admin/${id}/identified-export${scopeQuery(scope)}`, { headers: authHeaders() }).then(handle),
};

export default surveyApi;
